
import React, { useEffect, useState } from "react";
import "./recovery.css";

const BASE = (
  process.env.REACT_APP_API_BASE_URL || ""
).replace(/\/$/, "");

const SECURITY = `${BASE}/api/security/2fa/`;

function getCsrf() {
  const match = document.cookie.match(
    /(?:^|; )csrftoken=([^;]*)/
  );

  return match ? decodeURIComponent(match[1]) : "";
}

export default function PrivilegedAuthenticatorRecovery({
  mode = "platform"
}) {
  const isDjangoAdmin = mode === "django";

  const [targets, setTargets] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const targetRole = isDjangoAdmin
    ? "Platform Super Admin"
    : "Django Superuser";

  useEffect(() => {
    let active = true;

    async function loadTargets() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `${SECURITY}recovery/password-target/`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store"
          }
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data.detail ||
            `Unable to load accounts (${response.status})`
          );
        }

        if (active) {
          setTargets(
            Array.isArray(data.targets) ? data.targets : []
          );
        }
      } catch (err) {
        if (active) {
          setError(err.message || "Unable to load accounts.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadTargets();

    return () => {
      active = false;
    };
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();

    setError("");
    setSuccess(null);

    const selected = targets.find(
      item => String(item.user_id) === String(targetId)
    );

    if (!selected) {
      setError("Please select an authorized account.");
      return;
    }

    if (!password || !/^[0-9]{6}$/.test(otp)) {
      setError(
        "Your current password and a fresh six-digit OTP are required."
      );
      return;
    }

    if (!confirmed) {
      setError("Please confirm the recovery action.");
      return;
    }

    const proceed = window.confirm(
      `Reset the authenticator for ${selected.username}?\n\n` +
      "This will revoke their active sessions, authenticator " +
      "devices and recovery codes. They must enroll a new " +
      "authenticator before accessing their account."
    );

    if (!proceed) return;

    setBusy(true);

    try {
      if (!getCsrf()) {
        const csrfResponse = await fetch(
          `${SECURITY}django-admin/csrf/`,
          {
            method: "GET",
            credentials: "include"
          }
        );

        if (!csrfResponse.ok) {
          throw new Error("Unable to initialize CSRF protection.");
        }
      }

      const csrf = getCsrf();

      if (!csrf) {
        throw new Error("CSRF token unavailable.");
      }

      const response = await fetch(
        `${SECURITY}recovery/reset/`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrf
          },
          body: JSON.stringify({
            target_user_id: Number(selected.user_id),
            actor_password: password,
            actor_otp: otp
          })
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.detail ||
          `Authenticator reset failed (${response.status})`
        );
      }

      setSuccess({
        username: selected.username,
        auditId: data.audit_id,
        sessionsRevoked: data.sessions_revoked
      });

      setTargetId("");
      setPassword("");
      setOtp("");
      setConfirmed(false);

    } catch (err) {
      setError(err.message || "Authenticator reset failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="fh-recovery-panel">
      <div className="fh-recovery-heading">
        <div className="fh-recovery-icon">✦</div>

        <div>
          <p className="fh-recovery-kicker">
            PRIVILEGED ACCOUNT SECURITY
          </p>

          <h2>{targetRole} Authenticator Recovery</h2>

          <p>
            Select an authorized {targetRole} account
            that needs authenticator recovery.
          </p>
        </div>
      </div>

      <div className="fh-recovery-warning">
        <strong>Restricted security operation</strong>

        <p>
          This action permanently removes the selected account's
          authenticator devices and recovery codes, revokes known
          database-backed sessions, and requires new authenticator
          enrollment. The account password remains unchanged.
        </p>
      </div>

      <form
        className="fh-recovery-form"
        onSubmit={handleSubmit}
      >
        <p className="fh-recovery-note">
          {loading
            ? "Loading authorized accounts..."
            : `${targets.length} eligible ${targetRole} account(s).`}
        </p>

        <label>
          Select {targetRole}

          <select
            required
            value={targetId}
            onChange={event => {
              setTargetId(event.target.value);
              setConfirmed(false);
              setSuccess(null);
            }}
            disabled={loading || busy}
          >
            <option value="">
              {loading
                ? "Loading accounts..."
                : `Select ${targetRole}`}
            </option>

            {targets.map(account => (
              <option
                key={account.user_id}
                value={account.user_id}
              >
                {account.username}
              </option>
            ))}
          </select>
        </label>

        <label>
          Your current administrator password

          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={event => setPassword(event.target.value)}
            disabled={busy}
          />
        </label>

        <label>
          Fresh six-digit authenticator OTP

          <input
            type="text"
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={otp}
            onChange={event => {
              setOtp(
                event.target.value.replace(/\D/g, "").slice(0, 6)
              );
            }}
            disabled={busy}
          />
        </label>

        <label className="fh-recovery-check">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={event => setConfirmed(event.target.checked)}
            disabled={busy}
          />

          <span>
            I have verified the account owner's identity and
            understand that their existing authenticator and
            recovery codes will be revoked.
          </span>
        </label>

        {error && (
          <p
            className="fh-recovery-error"
            role="alert"
          >
            {error}
          </p>
        )}

        {success && (
          <p
            className="fh-recovery-success"
            role="status"
          >
            Authenticator reset completed for{" "}
            <strong>{success.username}</strong>.
            They must enroll a new authenticator.
            Audit ID: {success.auditId}.
            Sessions revoked: {success.sessionsRevoked}.
          </p>
        )}

        <button
          type="submit"
          className="fh-recovery-primary"
          disabled={
            loading ||
            busy ||
            !targetId ||
            !password ||
            otp.length !== 6 ||
            !confirmed
          }
        >
          {busy
            ? "Resetting securely..."
            : "Reset Selected Authenticator"}
        </button>
      </form>
    </section>
  );
}
